import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { OfflineNotice, useOnline } from "@/components/OfflineGate";
import { SearchQuotaLine, SearchPacks, BuyPackDialog, NoBalanceDialog } from "@/components/SearchQuota";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useWallet } from "@/lib/wallet";
import { Search, Sparkles, Loader2, ExternalLink } from "lucide-react";

const FALLBACK_IMG = "https://images.pexels.com/photos/7641028/pexels-photo-7641028.jpeg";

export default function AISearchDialog({ open, onOpenChange, onPickTitle, scope = null }) {
  const online = useOnline();
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [quota, setQuota] = useState(null);
  const [buyPack, setBuyPack] = useState(null);
  const [noBalance, setNoBalance] = useState(false);
  const navigate = useNavigate();
  const wallet = useWallet();

  useEffect(() => {
    if (!open) {
      setQuery("");
      setData(null);
      setBusy(false);
      setBuyPack(null);
      setNoBalance(false);
    } else if (navigator.onLine) {
      api.get("/search/quota").then((r) => setQuota(r.data)).catch(() => {});
    }
  }, [open]);

  const scoped = !!scope?.title_id;
  const exhausted = !!quota && quota.remaining === 0 && (quota.credits || 0) === 0;

  const runSearch = async (buyPack) => {
    const q = query.trim();
    setBusy(true);
    setData(null);
    try {
      const base = scoped ? { query: q, title_id: scope.title_id } : { query: q };
      const r = await api.post("/search", buyPack ? { ...base, buy_pack: buyPack } : base);
      setData(r.data);
      if (r.data.quota) setQuota(r.data.quota);
      if (buyPack) window.dispatchEvent(new CustomEvent("notelosh:wallet-changed"));
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "quota_exceeded") setQuota((x) => (x ? { ...x, remaining: 0, credits: 0 } : x));
      else if (code === "insufficient_balance") setNoBalance(true);
      else setData({ results: [], summary: "Search failed. Try again." });
    } finally {
      setBusy(false);
    }
  };

  const buyOnly = async (packId) => {
    setBusy(true);
    try {
      const r = await api.post("/search/buy", { pack: packId });
      setQuota(r.data);
      toast.success("Searches added to your account");
      window.dispatchEvent(new CustomEvent("notelosh:wallet-changed"));
    } catch (err) {
      if (err?.response?.data?.detail?.code === "insufficient_balance") setNoBalance(true);
      else setData({ results: [], summary: "Purchase failed. Try again." });
    } finally {
      setBusy(false);
    }
  };

  const payByCard = async () => {
    const p = buyPack;
    setBuyPack(null);
    setBusy(true);
    try {
      const r = await api.post("/search/buy/checkout", { pack: p.id, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start card checkout");
      setBusy(false);
    }
  };

  const confirmBuy = () => {
    const p = buyPack;
    setBuyPack(null);
    if (exhausted && query.trim()) runSearch(p.id); else buyOnly(p.id);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!query.trim() || busy || exhausted) return;
    runSearch(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="ai-search-dialog"
        data-scope={scoped ? "thread" : "all"}
        className="bg-[#121212] border-[#27272A] text-[#F3F4F6] max-w-2xl p-0 overflow-hidden"
      >
        {!online ? <OfflineNotice feature="AI search" compact /> : (<>
        <form onSubmit={submit} className="border-b border-[#27272A]">
          <div className="flex items-center gap-3 pl-5 pr-12 py-4">
            {busy ? (
              <Loader2 className="h-5 w-5 text-[#3B82F6] animate-spin" />
            ) : (
              <Sparkles className="h-5 w-5 text-[#3B82F6]" />
            )}
            <input
              autoFocus
              data-testid="ai-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                scoped
                  ? `Ask about "${scope.name}"…`
                  : "Ask anything across all notes — e.g. 'that React hooks article'"
              }
              className="flex-1 bg-transparent outline-none text-base placeholder:text-[#6B7280]"
            />
            <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-[#0A0A0A] border border-[#27272A] font-mono">↵</kbd>
          </div>
          {quota && <SearchQuotaLine quota={quota} />}
          {scoped && (
            <div
              data-testid="ai-search-scope-pill"
              className="px-5 pb-3 flex items-center gap-2 text-[11px] text-[#3B82F6]"
            >
              <span className="px-2 py-0.5 rounded-full bg-[#2563EB]/15 border border-[#2563EB]/30">
                Scoped to <b className="text-[#F3F4F6]">{scope.name}</b>
              </span>
              <span className="text-[#6B7280]">— results only from this note</span>
            </div>
          )}
        </form>

        <div className="max-h-[60vh] overflow-y-auto">
          {exhausted && !busy && (
            <SearchPacks packs={quota.packs || []} busy={busy} onPick={setBuyPack} exhausted />
          )}
          {!data && !busy && !exhausted && (
            <div className="px-5 py-10 text-center text-sm text-[#6B7280]">
              <Search className="h-6 w-6 mx-auto mb-2 text-[#27272A]" />
              {scoped
                ? `Ask AI to find anything inside "${scope.name}".`
                : "Search across all your notes by meaning, not keywords."}
            </div>
          )}
          {busy && (
            <div className="px-5 py-10 text-center text-sm text-[#9CA3AF]">
              Thinking with GPT-5.2…
            </div>
          )}
          {data && (
            <div className="px-5 py-4 space-y-3">
              {data.summary && (
                <div data-testid="ai-search-summary" className="text-sm text-[#D1D5DB] bg-[#1E1E1E] border border-[#27272A] rounded-xl px-4 py-3 leading-relaxed">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#3B82F6] font-medium mb-1">
                    <Sparkles className="h-3 w-3" /> AI summary
                  </div>
                  {data.summary}
                </div>
              )}
              {data.results?.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-medium">
                    {data.results.length} result{data.results.length === 1 ? "" : "s"}
                  </div>
                  {data.results.map((it) => (
                    <ResultRow key={it.item_id} item={it} onPickTitle={onPickTitle} />
                  ))}
                </div>
              ) : (
                !data.summary && (
                  <div className="text-sm text-[#6B7280] text-center py-6">No matches.</div>
                )
              )}
            </div>
          )}
          {quota && !exhausted && !busy && (
            <SearchPacks packs={(quota.packs || []).filter((p) => p.id !== "single")} busy={busy} onPick={setBuyPack} />
          )}
        </div>
        </>)}
        <BuyPackDialog
          pack={buyPack}
          runsSearch={exhausted && !!query.trim()}
          onCancel={() => setBuyPack(null)}
          onConfirm={confirmBuy}
          onCard={payByCard}
          balance={wallet ? wallet.balance_cents || 0 : null}
        />
        <NoBalanceDialog
          open={noBalance}
          onCancel={() => setNoBalance(false)}
          onTopUp={() => { setNoBalance(false); onOpenChange(false); navigate("/wallet"); }}
        />
      </DialogContent>
    </Dialog>
  );
}

function ResultRow({ item, onPickTitle }) {
  const isLink = item.type === "link" && item.url;
  const p = item.preview || {};
  return (
    <button
      data-testid={`search-result-${item.item_id}`}
      onClick={() => onPickTitle({ title_id: item.title_id, item_id: item.item_id })}
      className="w-full text-left flex gap-3 p-3 rounded-xl bg-[#1E1E1E] hover:bg-[#27272A] border border-[#27272A] transition-all"
    >
      {isLink && (
        <img
          src={p.image || FALLBACK_IMG}
          alt=""
          className="h-14 w-14 object-cover rounded-lg shrink-0"
          onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-[11px] text-[#3B82F6] mb-1">
          <span className="px-1.5 py-0.5 rounded bg-[#0A0A0A] border border-[#27272A]">
            {item.title_name || "Title"}
          </span>
          {isLink && (
            <span className="flex items-center gap-1 text-[#9CA3AF]">
              <ExternalLink className="h-3 w-3" />
              {(() => { try { return new URL(item.url).hostname; } catch { return ""; } })()}
            </span>
          )}
        </div>
        <div className="text-sm font-medium text-[#F3F4F6] line-clamp-1">
          {isLink ? (p.title || item.url) : (item.content || "Note")}
        </div>
        <div className="text-xs text-[#9CA3AF] line-clamp-1">
          {isLink ? p.description : item.content}
        </div>
      </div>
    </button>
  );
}
